"use client";

import {
  useShowcaseGenerations,
  type ShowcaseGeneration,
  type ShowcaseGenerationResult,
} from "@/hooks/useShowcaseGenerations";
import { downloadWatermarkedShowcasePdf } from "@/lib/showcasePdf";
import { cn } from "@/lib/utils";
import { api } from "convex/_generated/api";
import { useQuery } from "convex/react";
import {
  BookOpen,
  ClipboardCheck,
  Download,
  Eye,
  Loader2,
  Mic,
  Pause,
  Play,
  Sparkles,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { MarketingTrackedLink } from "../MarketingTrackedLink";
import { LANDING_SECTION_CONTAINER, LANDING_SECTION_SPACING } from "../section-spacing";

type ShowcaseType = ShowcaseGeneration["type"];

type TypeMeta = {
  label: string;
  Icon: LucideIcon;
  iconClass: string;
  cardAccent: string;
};

const TYPE_META: Record<ShowcaseType, TypeMeta> = {
  lesson_plan: {
    label: "Lesson plan",
    Icon: BookOpen,
    iconClass: "bg-emerald-100 text-emerald-700 ring-emerald-200/70",
    cardAccent: "from-emerald-400/80 to-emerald-200/40",
  },
  quiz: {
    label: "Quiz",
    Icon: ClipboardCheck,
    iconClass: "bg-yellow-100 text-yellow-700 ring-yellow-200/80",
    cardAccent: "from-yellow-300/90 to-yellow-100/60",
  },
  test: {
    label: "Test",
    Icon: ClipboardCheck,
    iconClass: "bg-lime-100 text-lime-700 ring-lime-200/80",
    cardAccent: "from-lime-300/90 to-emerald-100/60",
  },
  audio: {
    label: "Audio",
    Icon: Mic,
    iconClass: "bg-slate-900 text-primary ring-slate-300/80",
    cardAccent: "from-slate-700/90 to-slate-300/60",
  },
};

const SHOWCASE_MONTHLY_FALLBACK = 247;
const SHOWCASE_STATS_NOW_MS = Date.now();

type ShowcaseStats = {
  generatedLast30Days: number | null;
  periodDays: number;
  source: "admin_daily_stats" | "fallback";
};

function formatShortDate(createdAt: number): string {
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
  }).format(new Date(createdAt));
}

function formatCount(value: number): string {
  return new Intl.NumberFormat("en").format(value);
}

function ShowcaseSkeleton() {
  const placeholders = Array.from({ length: 8 });
  return (
    <section
      className={`relative isolate overflow-hidden bg-white text-slate-950 ${LANDING_SECTION_SPACING}`}
      aria-label="Loading generated output previews"
    >
      <div className={`relative z-10 ${LANDING_SECTION_CONTAINER}`}>
        <div className="mx-auto max-w-3xl text-center">
          <div className="mx-auto h-5 w-36 rounded-full bg-emerald-100" />
          <div className="mx-auto mt-4 h-9 w-full max-w-xl rounded-2xl bg-slate-100" />
          <div className="mx-auto mt-3 h-5 w-full max-w-2xl rounded-xl bg-slate-100" />
        </div>
        <div className="mt-8 overflow-hidden">
          <div className="flex w-max gap-4">
            {placeholders.map((_, index) => (
              <div
                key={index}
                className="h-[230px] w-[300px] animate-pulse rounded-2xl border border-emerald-100 bg-emerald-50/50"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function GenerationCard({
  item,
  onOpen,
}: {
  item: ShowcaseGeneration;
  onOpen: () => void;
}) {
  const meta = TYPE_META[item.type];
  const Icon = meta.Icon;

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${meta.label}: ${item.title}. Generated by ${item.teacherFirstName}. Open preview.`}
      className="group relative flex h-[210px] w-[292px] shrink-0 flex-col overflow-hidden rounded-2xl border border-emerald-100 bg-white p-4 text-left shadow-sm shadow-emerald-950/[0.04] ring-1 ring-emerald-950/[0.03] transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-1 hover:border-emerald-300 hover:shadow-xl hover:shadow-emerald-950/[0.08] focus:outline-none focus:ring-2 focus:ring-emerald-400 sm:w-[320px]"
    >
      <div className={cn("absolute inset-x-0 top-0 h-1 bg-gradient-to-r", meta.cardAccent)} />
      <div className="flex items-start gap-4">
        <span
          className={cn(
            "flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl ring-1",
            meta.iconClass,
          )}
        >
          <Icon className="h-8 w-8" strokeWidth={2.2} />
        </span>

        <div className="min-w-0 flex-1 pt-0.5">
          <div className="flex min-w-0 items-center justify-between gap-3">
            <span className="truncate text-xs font-black uppercase tracking-[0.14em] text-emerald-800">
              {meta.label}
            </span>
            <span className="shrink-0 text-[11px] font-medium text-slate-400">
              {formatShortDate(item.createdAt)}
            </span>
          </div>
          <p className="mt-2 truncate text-[12px] font-normal text-slate-500">
            Generated by {item.teacherFirstName}
          </p>
        </div>
      </div>

      <h3 className="mt-5 truncate text-lg font-black leading-tight tracking-tight text-slate-950">
        {item.title}
      </h3>

      <p className="mt-2 truncate text-sm leading-6 text-slate-600">
        {item.previewContent}
      </p>

      <span className="mt-auto flex justify-end">
        <span className="inline-flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 transition-[opacity,transform,background-color,border-color] duration-200 group-hover:bg-emerald-100 sm:w-auto sm:px-3 sm:opacity-0 sm:translate-y-1 sm:group-hover:translate-y-0 sm:group-hover:opacity-100 sm:group-focus-visible:translate-y-0 sm:group-focus-visible:opacity-100">
          <Eye className="h-4 w-4 shrink-0" />
          <span className="ml-2 hidden whitespace-nowrap text-sm font-semibold sm:inline">
            Open preview
          </span>
        </span>
      </span>
    </button>
  );
}

function MarqueeRow({
  items,
  reverse = false,
  onOpen,
}: {
  items: ShowcaseGeneration[];
  reverse?: boolean;
  onOpen: (item: ShowcaseGeneration) => void;
}) {
  const loop = [...items, ...items];
  return (
    <div className="landing-showcase-marquee overflow-hidden py-2">
      <div
        className={cn(
          "landing-showcase-track flex w-max gap-4",
          reverse && "landing-showcase-track-reverse",
        )}
      >
        {loop.map((item, index) => (
          <GenerationCard
            key={`${item.fullResultRef.table}-${item.fullResultRef.id}-${index}`}
            item={item}
            onOpen={() => onOpen(item)}
          />
        ))}
      </div>
    </div>
  );
}

function LimitedAudioPreview({
  url,
  seconds,
}: {
  url: string;
  seconds: number;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleTimeUpdate = () => {
      if (audio.currentTime >= seconds) {
        audio.pause();
        audio.currentTime = seconds;
      }
      setCurrentTime(Math.min(audio.currentTime, seconds));
    };
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    return () => {
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [seconds, url]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    if (isPlaying) {
      audio.pause();
      return;
    }
    if (audio.currentTime >= seconds) {
      audio.currentTime = 0;
      setCurrentTime(0);
    }
    void audio.play();
  };

  const progress = Math.max(0, Math.min(1, currentTime / seconds));

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-950 p-4 text-white">
      <audio ref={audioRef} src={url} preload="metadata" className="hidden" />
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={togglePlay}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform active:scale-95"
          aria-label={isPlaying ? "Pause audio preview" : "Play audio preview"}
        >
          {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="h-2 overflow-hidden rounded-full bg-white/15">
            <div
              className="h-full rounded-full bg-primary transition-[width]"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
          <div className="mt-2 flex justify-between text-xs text-white/55">
            <span>{Math.round(currentTime)}s</span>
            <span>{seconds}s preview</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewModal({
  active,
  onClose,
}: {
  active: ShowcaseGeneration;
  onClose: () => void;
}) {
  const detail = useQuery(api.showcaseGenerations.getShowcaseGenerationResult, {
    fullResultRef: active.fullResultRef,
  }) as ShowcaseGenerationResult | null | undefined;
  const meta = TYPE_META[active.type];
  const Icon = meta.Icon;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleDownloadPdf = () => {
    if (!detail || detail.type === "audio") {
      return;
    }
    downloadWatermarkedShowcasePdf({
      title: detail.title,
      subtitle: `Generated by ${detail.teacherFirstName} on darsi.ma`,
      sections: detail.contentSections,
    });
  };

  const resolvedDetail: ShowcaseGenerationResult | null = detail ?? null;
  const isAudio = resolvedDetail?.type === "audio";

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/45 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="relative flex max-h-[92dvh] w-full max-w-4xl flex-col overflow-hidden rounded-t-3xl border border-emerald-100 bg-white shadow-2xl shadow-slate-950/20 sm:rounded-3xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="showcase-preview-title"
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-emerald-100 bg-emerald-50/70 px-5 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className={cn("flex h-8 w-8 items-center justify-center rounded-xl", meta.iconClass)}>
                <Icon className="h-4 w-4" />
              </span>
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-emerald-800">
                {meta.label}
              </span>
            </div>
            <h3
              id="showcase-preview-title"
              className="mt-2 line-clamp-2 text-lg font-black tracking-tight text-slate-950 sm:text-xl"
            >
              {active.title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-emerald-200 bg-white text-slate-700 transition-colors hover:bg-emerald-50"
            aria-label="Close preview"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
          {detail === undefined ? (
            <div className="flex min-h-64 items-center justify-center gap-3 text-sm font-medium text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
              Loading preview
            </div>
          ) : resolvedDetail ? (
            <div className="space-y-5">
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4">
                <p className="text-sm font-semibold text-slate-950">
                  Generated by {resolvedDetail.teacherFirstName}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">
                  {resolvedDetail.previewContent}
                </p>
              </div>

              {resolvedDetail.audioPreviewUrl && resolvedDetail.audioPreviewSeconds ? (
                <LimitedAudioPreview
                  url={resolvedDetail.audioPreviewUrl}
                  seconds={resolvedDetail.audioPreviewSeconds}
                />
              ) : null}

              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                <div className="space-y-5">
                  {resolvedDetail.contentSections.map((section, sectionIndex) => (
                    <section key={`${section.heading}-${sectionIndex}`}>
                      <h4 className="text-sm font-black uppercase tracking-[0.12em] text-emerald-800">
                        {section.heading}
                      </h4>
                      <div className="mt-2 space-y-2 text-sm leading-relaxed text-slate-700">
                        {section.body.map((line, lineIndex) => (
                          <p key={`${line.slice(0, 32)}-${lineIndex}`} className="whitespace-pre-wrap">
                            {line}
                          </p>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
              This preview is no longer available.
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-emerald-100 bg-white px-5 py-4">
          <p className="text-xs font-medium text-slate-500">
            {isAudio ? "Audio previews are limited and cannot be downloaded here." : "PDF exports include a visible preview watermark."}
          </p>
          <div className="flex items-center gap-2">
            {!isAudio ? (
              <button
                type="button"
                onClick={handleDownloadPdf}
                disabled={!resolvedDetail}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 text-sm font-bold text-emerald-800 transition-[transform,background-color,opacity] hover:bg-emerald-100 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                Download PDF
              </button>
            ) : null}
            <MarketingTrackedLink
              href="/sign-up"
              ctaId="showcase_preview_modal"
              ctaLabel="Create one like this"
              className="inline-flex min-h-10 items-center justify-center rounded-xl bg-[#08A62F] px-4 text-sm font-bold text-white transition-[transform,background-color] hover:bg-[#078F2B] active:scale-95"
            >
              Create one like this
            </MarketingTrackedLink>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex min-h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function GenerationsMarquee() {
  const { generations, isLoading, shouldShow } = useShowcaseGenerations(30);
  const showcaseStats = useQuery(
    api.showcaseGenerations.getShowcaseStats,
    { nowMs: SHOWCASE_STATS_NOW_MS },
  ) as ShowcaseStats | undefined;
  const [active, setActive] = useState<ShowcaseGeneration | null>(null);
  const generatedCount =
    showcaseStats?.generatedLast30Days || SHOWCASE_MONTHLY_FALLBACK;
  const generatedPeriodDays = showcaseStats?.periodDays || 30;

  const rows = useMemo(() => {
    const midpoint = Math.ceil(generations.length / 2);
    const first = generations.slice(0, midpoint);
    const second = generations.slice(midpoint);
    return [first, second.length >= 3 ? second : generations.slice().reverse()];
  }, [generations]);

  if (isLoading) {
    return <ShowcaseSkeleton />;
  }

  if (!shouldShow) {
    return null;
  }

  return (
    <section
      className={`relative isolate overflow-hidden bg-white text-slate-950 ${LANDING_SECTION_SPACING}`}
      aria-labelledby="generations-marquee-heading"
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-200 to-transparent"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-emerald-200 to-transparent"
        aria-hidden
      />

      <div className={`relative z-10 ${LANDING_SECTION_CONTAINER}`}>
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-black uppercase tracking-[0.16em] text-emerald-800">
            <Sparkles className="h-3.5 w-3.5" />
            Live generations
          </div>
          <h2
            id="generations-marquee-heading"
            className="mt-4 text-[38px] font-black leading-[1.05] tracking-tight text-slate-950 sm:text-[54px] lg:text-[62px]"
          >
            Real classroom content teachers made with darsi.ma.
          </h2>
          <p className="mx-auto mt-5 max-w-[790px] text-pretty text-lg font-medium leading-[1.45] text-slate-600 sm:text-xl">
            <span className="font-black text-[#08A62F]">
              {formatCount(generatedCount)}
            </span>{" "}
            lesson plans, quizzes, tests, and audio tracks generated in the last{" "}
            {generatedPeriodDays} days.
          </p>
        </div>

        <div className="relative mt-8 sm:mt-10">
          <div
            className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-white to-transparent sm:w-28"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-white to-transparent sm:w-28"
            aria-hidden
          />
          <MarqueeRow items={rows[0]} onOpen={setActive} />
          <MarqueeRow items={rows[1]} reverse onOpen={setActive} />
        </div>
      </div>

      {active ? <PreviewModal active={active} onClose={() => setActive(null)} /> : null}
    </section>
  );
}
